import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { Button } from '@agms/ui'
import { useAuth } from '../../hooks/useAuth'
import {
  useAppointmentAssignees,
  useAppointmentSettings,
  useCancelAppointment,
  useCompleteAppointment,
  useCreateAppointment,
  useNoShowAppointment,
  useRescheduleAppointment,
  useUpdateAppointment,
} from '../../hooks/useAppointments'
import { apiErrorMessage, appointmentScheduleError } from '../../hooks/useErrorHandler'
import type { AccountAnchor, Appointment } from '../../lib/api'
import { formatAppointmentWhen } from '../../lib/formatAppointmentWhen'
import {
  EDIT_REMINDER_HINT,
  appointmentAccountLabel,
  appointmentAccountPath,
  appointmentFormFromAppointment,
  appointmentUpdateFromForm,
  canAssignAppointment,
  canEditAppointment,
  canReadAppointmentSettings,
  canSeeAppointmentLocation,
  canSeeAppointmentNotes,
  emptyAppointmentForm,
  parseAppointmentForm,
  reminderHint,
  reminderLabel,
  statusLabel,
  type AppointmentFormState,
} from '../../lib/appointmentForm'
import { AccountSearchField, type SelectedAccount } from './AccountSearchField'
import { AppointmentForm } from './AppointmentForm'

type AppointmentDialogProps = {
  onClose: () => void
} & (
  | { mode: 'create'; initialStartsAt: string; anchor?: AccountAnchor }
  | { mode: 'appointment'; appointment: Appointment }
)

export function AppointmentDialog(props: AppointmentDialogProps) {
  const { onClose } = props
  const appointment = props.mode === 'appointment' ? props.appointment : null
  const fixedAnchor = props.mode === 'create' ? props.anchor : undefined
  const { user } = useAuth()
  const canAssign = canAssignAppointment(user)
  const canReadSettings = canReadAppointmentSettings(user)
  const canEdit = appointment ? canEditAppointment(user, appointment) : false
  const showLocation = appointment ? canSeeAppointmentLocation(user, appointment) : true
  const showNotes = appointment ? canSeeAppointmentNotes(user, appointment) : true
  const showForm = props.mode === 'create' || canEdit

  const settingsQuery = useAppointmentSettings(props.mode === 'create' && canReadSettings)
  const assigneesQuery = useAppointmentAssignees(canAssign && showForm)
  const accountAnchor: AccountAnchor | undefined = appointment
    ? { kind: appointment.appointable_type, id: appointment.appointable_id }
    : fixedAnchor
  const createAppointment = useCreateAppointment()
  const updateAppointment = useUpdateAppointment(accountAnchor)
  const rescheduleAppointment = useRescheduleAppointment(accountAnchor)
  const cancelAppointment = useCancelAppointment(accountAnchor)
  const completeAppointment = useCompleteAppointment(accountAnchor)
  const noShowAppointment = useNoShowAppointment(accountAnchor)

  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)
  const [values, setValues] = useState<AppointmentFormState>(() => (
    appointment
      ? appointmentFormFromAppointment(appointment)
      : emptyAppointmentForm(props.mode === 'create' ? props.initialStartsAt : '')
  ))
  const [selected, setSelected] = useState<SelectedAccount | null>(null)
  const [error, setError] = useState<string | null>(null)

  const title = props.mode === 'create'
    ? 'New appointment'
    : canEdit
      ? 'Edit appointment'
      : 'Appointment'

  const busy = createAppointment.isPending
    || updateAppointment.isPending
    || rescheduleAppointment.isPending
    || cancelAppointment.isPending
    || completeAppointment.isPending
    || noShowAppointment.isPending

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const firstControl = dialogRef.current?.querySelector<HTMLElement>(
      'input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled])',
    )
    firstControl?.focus()

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onCloseRef.current()
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      previousFocus?.focus()
    }
  }, [])

  const saveCreate = () => {
    const anchor = fixedAnchor ?? selected?.anchor
    if (!anchor) {
      setError('Choose a lead or merchant.')
      return
    }
    const parsed = parseAppointmentForm(values)
    if ('error' in parsed) {
      setError(parsed.error)
      return
    }
    setError(null)
    createAppointment.mutate(
      { anchor, payload: parsed.payload },
      {
        onSuccess: onClose,
        onError: (err) => setError(appointmentScheduleError(anchor.kind, err)),
      },
    )
  }

  const saveEdit = () => {
    if (!appointment || !user) return
    const diff = appointmentUpdateFromForm(appointment, values, {
      canAssign,
      currentUserId: user.id,
      includeLocation: showLocation,
      includeNotes: showNotes,
    })
    if (diff.error) {
      setError(diff.error)
      return
    }
    if (Object.keys(diff.update).length === 0 && !diff.rescheduleAt) {
      setError('No changes to save.')
      return
    }
    setError(null)

    const reschedule = () => {
      if (!diff.rescheduleAt) {
        onClose()
        return
      }
      rescheduleAppointment.mutate(
        { appointmentId: appointment.id, startsAt: diff.rescheduleAt },
        {
          onSuccess: onClose,
          onError: (err) => setError(apiErrorMessage(err, 'Unable to reschedule.')),
        },
      )
    }

    if (Object.keys(diff.update).length === 0) {
      reschedule()
      return
    }

    updateAppointment.mutate(
      { appointmentId: appointment.id, payload: diff.update },
      {
        onSuccess: reschedule,
        onError: (err) => setError(apiErrorMessage(err, 'Unable to save the appointment.')),
      },
    )
  }

  const runStatus = (
    action: { mutate: (id: string, options: { onSuccess: () => void; onError: (err: unknown) => void }) => void },
    fallback: string,
  ) => {
    if (!appointment) return
    setError(null)
    action.mutate(appointment.id, {
      onSuccess: onClose,
      onError: (err) => setError(apiErrorMessage(err, fallback)),
    })
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4 py-6"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="appointment-dialog-title"
        className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl"
      >
        <div className="border-b border-slate-200 px-5 py-4">
          <h2 id="appointment-dialog-title" className="text-lg font-semibold text-slate-900">{title}</h2>
        </div>
        <div className="space-y-4 px-5 py-4">
          {appointment && (
            <div className="space-y-1 text-sm text-slate-700">
              <Link
                to={appointmentAccountPath(appointment)}
                className="font-medium text-brand-700 underline"
              >
                {appointmentAccountLabel(appointment)}
              </Link>
              <p>
                <span className="capitalize">{appointment.type}</span>
                {' · '}
                {statusLabel(appointment.status)}
              </p>
              {!showForm && (
                <dl className="space-y-1">
                  <div>
                    <dt className="text-xs text-slate-500">When</dt>
                    <dd>{formatAppointmentWhen(appointment.starts_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Duration</dt>
                    <dd>{appointment.duration_minutes} minutes</dd>
                  </div>
                  {showLocation && (
                    <div>
                      <dt className="text-xs text-slate-500">Location</dt>
                      <dd>{appointment.location || 'None'}</dd>
                    </div>
                  )}
                  {showNotes && (
                    <div>
                      <dt className="text-xs text-slate-500">Notes</dt>
                      <dd className="whitespace-pre-wrap">{appointment.notes || 'None'}</dd>
                    </div>
                  )}
                  <div>
                    <dt className="text-xs text-slate-500">Assignee</dt>
                    <dd>{appointment.assigned_user?.name ?? 'Unassigned'}</dd>
                  </div>
                  <div>
                    <dt className="text-xs text-slate-500">Reminder</dt>
                    <dd>{reminderLabel(appointment.reminder_offset_minutes)}</dd>
                  </div>
                </dl>
              )}
              {showForm && (
                <p className="text-xs text-slate-500">{reminderLabel(appointment.reminder_offset_minutes)}</p>
              )}
              {showForm && !canAssign && appointment.assigned_user && (
                <p className="text-slate-600">Assignee: {appointment.assigned_user.name}</p>
              )}
            </div>
          )}

          {props.mode === 'create' && !fixedAnchor && (
            <AccountSearchField selected={selected} onSelect={setSelected} />
          )}

          {showForm && (
            <AppointmentForm
              values={values}
              onChange={setValues}
              onSubmit={props.mode === 'create' ? saveCreate : saveEdit}
              submitLabel={props.mode === 'create' ? 'Schedule appointment' : 'Save changes'}
              pending={createAppointment.isPending || updateAppointment.isPending || rescheduleAppointment.isPending}
              error={error}
              canAssign={canAssign}
              assignees={assigneesQuery.data ?? []}
              currentAssignee={appointment?.assigned_user ?? null}
              reminderHint={props.mode === 'create'
                ? reminderHint(canReadSettings, settingsQuery.data?.reminder_offset_minutes)
                : EDIT_REMINDER_HINT}
              showLocation={showLocation}
              showNotes={showNotes}
              framed={false}
            />
          )}

          {!showForm && error && <p className="text-sm text-red-600" role="alert">{error}</p>}

          {canEdit && appointment && (
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={busy}
                isLoading={completeAppointment.isPending}
                onClick={() => runStatus(completeAppointment, 'Unable to complete the appointment.')}
              >
                Complete
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={busy}
                isLoading={noShowAppointment.isPending}
                onClick={() => runStatus(noShowAppointment, 'Unable to mark no-show.')}
              >
                No-show
              </Button>
              <Button
                type="button"
                size="sm"
                variant="secondary"
                disabled={busy}
                isLoading={cancelAppointment.isPending}
                onClick={() => runStatus(cancelAppointment, 'Unable to cancel the appointment.')}
              >
                Cancel appointment
              </Button>
            </div>
          )}

          <div className="flex justify-end">
            <Button type="button" variant="ghost" onClick={onClose}>Close</Button>
          </div>
        </div>
      </div>
    </div>
  )
}
